import { cssMap } from '@atlaskit/css';

import Heading from '@atlaskit/heading/heading';

import Link from '@atlaskit/link/link';

import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import { token } from '@atlaskit/tokens';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { JiraSideNavMessage } from '@post-office/components--jira-side-nav-message';

import { useEffect, useRef } from 'react';

import { FormattedMessage } from 'react-intl';

import { type JSX } from 'react/jsx-runtime';

import { ExpertHelpSpotIllustration } from './ExpertHelpSpotIllustration';

import { messages } from './messages';

const ANALYTICS_ATTRIBUTES = { messageTemplateId: 'servco-recent-purchase-in-product-recruitment' };

const BOOKING_URL =
	'https://atlassian.zoom.us/zbook/d/1c9qynjc/atlassian-service-collection-signup-and-setup-review';

const SITE_ADDRESS_PARAM = 'whatsyouatlassiansiteaddressegacmeatlassiannet';

/**
 * Prefills the site address so the admin does not have to type it. Omitted when the
 * address could not be resolved, leaving the field blank rather than wrong.
 */
const buildBookingUrl = (siteAddress: string): string => {
	if (!siteAddress) {
		return BOOKING_URL;
	}

	const url = new URL(BOOKING_URL);
	url.searchParams.set(SITE_ADDRESS_PARAM, siteAddress);
	return url.toString();
};

const styles = cssMap({
	body: {
		paddingBlock: token('space.050'),
	},
	content: {
		flexGrow: 1,
		minWidth: 0,
		paddingInlineEnd: token('space.400'),
	},
	link: {
		display: 'flex',
		alignItems: 'center',
		minHeight: '24px',
	},
});

export type Props = {
	onDismissed: () => void;
	/** Site address without a scheme, e.g. `acme.atlassian.net`. Empty if unresolved. */
	siteAddress: string;
};

const whyAmISeeingThisReasons = [
	<FormattedMessage {...messages.reasonRecentPurchase} />,
	<FormattedMessage {...messages.reasonUserCount} />,
];

const whyAmISeeingThisModalContent = {
	message: (
		<>
			<p>
				<FormattedMessage {...messages.whyAmISeeingThisIntro} />
			</p>
			<p>
				<FormattedMessage {...messages.whyAmISeeingThisBasedOn} />
			</p>
		</>
	),
	reasons: whyAmISeeingThisReasons,
};

export const ServiceCollectionRecruitmentBanner = ({
	onDismissed,
	siteAddress,
}: Props): JSX.Element => {
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const hasFiredViewedEventRef = useRef(false);

	useEffect(() => {
		if (hasFiredViewedEventRef.current) {
			return;
		}
		hasFiredViewedEventRef.current = true;

		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'viewed',
				actionSubject: 'banner',
				actionSubjectId: 'serviceCollectionRecruitmentBanner',
			},
			ANALYTICS_ATTRIBUTES,
		);
	}, [fireAnalyticsEvent]);

	const handleBookNowClicked = () => {
		fireAnalyticsEvent(
			{
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'bookNow',
			},
			ANALYTICS_ATTRIBUTES,
		);
	};

	return (
		<JiraSideNavMessage
			onDismissed={onDismissed}
			whyAmISeeingThisModalContent={whyAmISeeingThisModalContent}
			additionalAnalyticsAttributes={ANALYTICS_ATTRIBUTES}
			bodyContent={
				<Inline xcss={styles.body} space="space.100" alignBlock="start">
					<ExpertHelpSpotIllustration />
					<Box xcss={styles.content}>
						<Stack space="space.100">
							<Heading size="xsmall">
								<FormattedMessage {...messages.title} />
							</Heading>
							<Text as="p" color="color.text.subtle">
								<FormattedMessage {...messages.description} values={{ br: <br /> }} />
							</Text>
							<Box xcss={styles.link}>
								<Link
									href={buildBookingUrl(siteAddress)}
									target="_blank"
									onClick={handleBookNowClicked}
								>
									<FormattedMessage {...messages.bookNow} />
								</Link>
							</Box>
						</Stack>
					</Box>
				</Inline>
			}
		/>
	);
};
